import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { getControl } from '@shared/helpers/form.helper';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { formPaths } from '@shared/constants/form-paths.const';
import { getScopoSocialeControl } from '../validators/helpers/scopo-sociale-form-validators.helper';
import { cloneDeep } from 'lodash-es';
import { debounceTime, merge, startWith } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';
import { nz } from '@shared/helpers/utils.helper';

export const setScopoDatiBilancioListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_AttivoCirc(form, store);
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Attivo.my_StPaAtCItot2', 'my_rimanenze');
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Attivo.my_StPaAtCItot1', 'my_rimanenzeAnno1');
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Passivo.my_StPaPsAtot2', 'my_PatrimNetto');
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Passivo.my_StPaPsAtot1', 'my_PatrimNettoAnno1');
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Attivo.my_StPaAtBtot2', 'my_Immobilizzazioni');
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Attivo.my_StPaAtBtot1', 'my_ImmobilizzazioniAnno1');
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Attivo.my_StPaAtTOT2', 'my_CapitInvest');
    setValueFromDatiBilancioHelper(form, store, 'my_StatoPatrimoniale__Attivo.my_StPaAtTOT1', 'my_CapitInvestAnno1');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcAtot2', 'my_ValProd');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcAtot1', 'my_ValProdAnno1');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcBtot2', 'my_CostiProd');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcBtot1', 'my_CostiProdAnno1');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcCtot2', 'my_RisultGest');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcCtot1', 'my_RisultGestAnno1');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcA12', 'my_RicaviVendDep');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcA11', 'my_RicaviVendAnno1Dep');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcA12', 'my_RicaviVend');
    setValueFromDatiBilancioHelper(form, store, 'my_ContoEconomico.my_CoEcA11', 'my_RicaviVendAnno1');
    setRaggScopoSocialeDifference(form, store, 'my_ValProd', 'my_RicaviEccAnno2', 'my_ValProdDep');
    setRaggScopoSocialeDifference(form, store, 'my_ValProdAnno1', 'my_RicaviEccAnno1', 'my_ValProdAnno1Dep');
    setRaggScopoSocialeDifference(form, store, 'my_CostiProd', 'my_CostiEccAnno2', 'my_CostiProdDep');
    setRaggScopoSocialeDifference(form, store, 'my_CostiProdAnno1', 'my_CostiEccAnno1', 'my_CostiProdAnno1Dep');
};

const my_AttivoCirc = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getScopoSocialeControl(form, 'my_AttivoCirc');

    control.valueChanges.pipe(withInitialValueDebounced(control)).subscribe((value) => {
        const normalizedValue = typeof value === 'number' ? value.toString(10) : value;

        control.patchValue(normalizedValue, { emitEvent: false });

        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_RaggScopoSociale.my_AttivoCirc = normalizedValue as string;
            return myVerbaleClone;
        });
    });
};

const setValueFromDatiBilancioHelper = (
    form: FormGroup,
    store: Store<Revisione53State>,
    controlPath: string,
    raggSocialeTarget: string
): void => {
    const sourceControl = getControl(form, `${formPaths.datiBilancio}.${controlPath}`);
    const raggSocialeControl = getScopoSocialeControl(form, raggSocialeTarget);

    sourceControl.valueChanges.pipe(withInitialValueDebounced(sourceControl)).subscribe((sourceValue) => {
        const normalizedValue = typeof sourceValue === 'number' ? sourceValue.toString(10) : sourceValue;

        raggSocialeControl?.patchValue(normalizedValue, { emitEvent: false });

        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_RaggScopoSociale[raggSocialeTarget] = normalizedValue;
            return myVerbaleClone;
        });
    });
};

const setRaggScopoSocialeDifference = (
    form: FormGroup,
    store: Store<Revisione53State>,
    firstControlName: string,
    secondControlName: string,
    targetControlName: string
): void => {
    const firstControl = getScopoSocialeControl(form, firstControlName);
    const secondControl = getScopoSocialeControl(form, secondControlName);

    merge(firstControl.valueChanges.pipe(startWith(firstControl.value)), secondControl.valueChanges.pipe(startWith(secondControl.value)))
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const value = (nz(firstControl.value) - nz(secondControl.value)).toString(10);

            getScopoSocialeControl(form, targetControlName).patchValue(value, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_RaggScopoSociale[targetControlName] = value;
                return myVerbaleClone;
            });
        });
};
